import { useState, useCallback } from 'react';
import { useApi } from './useApi';
import { strategyEngineService } from '../services/strategyEngineService';

export const useStrategies = () => {
    const { loading, error, executeRequest, clearError } = useApi();
    const [strategies, setStrategies] = useState([]);
    const [total, setTotal] = useState(0);
    const [signalsToday, setSignalsToday] = useState(0);
    const [isAdmin, setIsAdmin] = useState(false);
    const [selectedStrategy, setSelectedStrategy] = useState(null);

    const fetchStrategies = useCallback(async (page = 1, limit = 10, filters = {}) => {
        try {
            const response = await executeRequest(strategyEngineService.getStrategies, page, limit, filters);
            setStrategies(response.data || []);
            setTotal(response.meta?.total || 0);
            setSignalsToday(response.meta?.signalsToday || 0);
            setIsAdmin(!!response.meta?.isAdmin);
            return response;
        } catch (err) {
            console.error('Failed to fetch strategies:', err);
            throw err;
        }
    }, [executeRequest]);

    const fetchStrategyById = useCallback(async (id) => {
        try {
            const response = await executeRequest(strategyEngineService.getStrategyById, id);
            setSelectedStrategy(response.data || null);
            return response;
        } catch (err) {
            console.error('Failed to fetch strategy:', err);
            throw err;
        }
    }, [executeRequest]);

    const createStrategy = useCallback(async (data) => {
        try {
            const response = await executeRequest(strategyEngineService.createStrategy, data);
            return response;
        } catch (err) {
            console.error('Failed to create strategy:', err);
            throw err;
        }
    }, [executeRequest]);

    const updateStrategy = useCallback(async (id, data) => {
        try {
            const response = await executeRequest(strategyEngineService.updateStrategy, id, data);
            return response;
        } catch (err) {
            console.error('Failed to update strategy:', err);
            throw err;
        }
    }, [executeRequest]);

    const deleteStrategy = useCallback(async (id) => {
        try {
            const response = await executeRequest(strategyEngineService.deleteStrategy, id);
            return response;
        } catch (err) {
            console.error('Failed to delete strategy:', err);
            throw err;
        }
    }, [executeRequest]);

    const toggleStrategy = useCallback(async (id) => {
        try {
            const response = await executeRequest(strategyEngineService.toggleStrategy, id);
            return response;
        } catch (err) {
            console.error('Failed to toggle strategy:', err);
            throw err;
        }
    }, [executeRequest]);

    const clearSelectedStrategy = useCallback(() => {
        setSelectedStrategy(null);
    }, []);

    // `symbol` is optional — the BacktestPage picks one when a
    // strategy has multiple symbols. For single-symbol strategies
    // (or legacy callers) it can be omitted and the backend uses
    // the strategy's only symbol as a fallback.
    const backtestStrategy = useCallback(async (id, startDate, endDate, symbol) => {
        try {
            const response = await executeRequest(strategyEngineService.backtestStrategy, id, startDate, endDate, symbol);
            return response;
        } catch (err) {
            console.error('Failed to backtest strategy:', err);
            throw err;
        }
    }, [executeRequest]);

    return {
        strategies,
        total,
        signalsToday,
        isAdmin,
        selectedStrategy,
        loading,
        error,
        fetchStrategies,
        fetchStrategyById,
        createStrategy,
        updateStrategy,
        deleteStrategy,
        toggleStrategy,
        backtestStrategy,
        clearSelectedStrategy,
        clearError,
    };
};
